/**
 * Facebook Pixel Block
 *
 * Injection block that loads the Meta/Facebook pixel tracking script and
 * forwards canonical Vibesite events from the runtime event bus into fbq.
 *
 * Supports multiple pixel IDs (comma-separated) on a single block instance.
 * Multi-instance safe (corporate + property scopes).
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockToken,
  useVibesiteEvents,
} from '@vibesites/runtime/react';
import { Event, type VibesiteEventName } from '@vibesites/runtime';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'facebook-pixel-script';

// ============================================================================
// Event mapping — canonical Vibesite event → fbq method + name + params
// ============================================================================

interface FbqEventMapping {
  /**
   * 'track' for standard FB events (Lead, Contact, ViewContent, ...);
   * 'trackCustom' for non-standard event names.
   */
  method: 'track' | 'trackCustom';
  name: string;
  params?: (payload: Record<string, unknown>) => Record<string, unknown>;
}

/**
 * EVENT_MAP is exported so the editor's "Tracked Events" panel can render it.
 */
export const FB_EVENT_MAP: Partial<Record<VibesiteEventName, FbqEventMapping>> = {
  [Event.contact_form_submitted]: {
    method: 'track',
    name: 'Lead',
    params: (p) => ({ content_name: p.formId }),
  },
  [Event.tour_scheduled]: {
    method: 'track',
    name: 'Schedule',
    params: (p) => ({
      content_name: p.tourType,
      content_category: 'tour',
    }),
  },
  [Event.apply_now_clicked]: {
    method: 'trackCustom',
    name: 'SubmitApplication',
    params: (p) => ({
      content_name: p.linkText,
      destination: p.href,
    }),
  },
  [Event.schedule_tour_clicked]: {
    method: 'trackCustom',
    name: 'ScheduleTourClick',
    params: (p) => ({
      content_name: p.linkText,
      destination: p.href,
    }),
  },
  [Event.self_guided_tour_clicked]: {
    method: 'trackCustom',
    name: 'SelfGuidedTourClick',
    params: (p) => ({
      content_name: p.linkText,
      destination: p.href,
    }),
  },
  [Event.virtual_tour_clicked]: {
    method: 'trackCustom',
    name: 'VirtualTourClick',
    params: (p) => ({
      content_name: p.linkText,
      destination: p.href,
    }),
  },
  [Event.resident_portal_clicked]: {
    method: 'trackCustom',
    name: 'ResidentPortalClick',
    params: (p) => ({ destination: p.href }),
  },
  [Event.phone_clicked]: {
    method: 'track',
    name: 'Contact',
    params: (p) => ({
      content_name: p.phoneNumber,
      content_category: 'phone',
    }),
  },
  [Event.email_clicked]: {
    method: 'track',
    name: 'Contact',
    params: (p) => ({
      content_name: p.emailAddress,
      content_category: 'email',
    }),
  },
};

function FacebookPixelComponent(_props: BlockProps) {
  const pixelIds = useBlockToken('pixel_ids', { defaultValue: '' });
  const { subscribe } = useVibesiteEvents();
  const trackingAllowed = useTrackingAllowed('advertising');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !pixelIds) return;

    if (document.getElementById(SCRIPT_ID)) return;

    const ids = pixelIds.split(',').map(id => id.trim()).filter(Boolean);
    if (ids.length === 0) return;

    const inits = ids.map(id => `fbq('init','${id}');`).join('');

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');${inits}fbq('track','PageView');`;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, pixelIds]);

  // Subscribe to canonical events and forward to fbq.
  useEffect(() => {
    if (!trackingAllowed || !pixelIds) return;

    const off = subscribe('*', (name, payload) => {
      const mapping = FB_EVENT_MAP[name];
      if (!mapping) return;

      const w = window as unknown as {
        fbq?: (method: string, eventName: string, params?: Record<string, unknown>) => void;
      };
      if (typeof w.fbq !== 'function') return;

      const params = mapping.params
        ? mapping.params(payload as unknown as Record<string, unknown>)
        : {};
      const cleanParams: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined) cleanParams[k] = v;
      }

      w.fbq(mapping.method, mapping.name, cleanParams);
    });

    return off;
  }, [trackingAllowed, pixelIds, subscribe]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'FacebookPixel',
  displayName: 'Facebook Pixel',
  tokens: {
    pixel_ids: {
      label: 'Pixel IDs',
      type: 'text',
      description: 'One or more Facebook Pixel IDs, comma-separated.',
      required: true,
    },
  },
};

export const facebookPixelBlock: BlockDefinition = {
  id: 'facebook-pixel',
  name: 'Facebook Pixel',
  description:
    'Meta/Facebook conversion tracking pixel. Loads fbevents.js and forwards Vibesite canonical events. Supports multiple pixel IDs.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: FacebookPixelComponent,
  contentSchema,
  tags: ['facebook', 'meta', 'pixel', 'tracking', 'ads', 'conversion'],
  trackedEvents: (Object.keys(FB_EVENT_MAP) as Array<keyof typeof FB_EVENT_MAP>)
    .filter((name): name is VibesiteEventName => !!FB_EVENT_MAP[name])
    .map((name) => {
      const m = FB_EVENT_MAP[name]!;
      return {
        event: name as VibesiteEventName,
        mapsTo: `fbq('${m.method}', '${m.name}')`,
      };
    }),
};
